import type { QueryParamsType } from './Api.types';

/** Universal Headers */
export const HEADER_CONTENT_TYPE = 'Content-Type';
/** Request Headers */
export const HEADER_AUTHORIZATION = 'Authorization';
export const HEADER_AUTHORIZATION_BEARER = 'Bearer'; // For AWS, "Bearer" must be uppercase (at least in 2015). See: https://github.com/golang/oauth2/issues/113#issuecomment-101060957
export const HEADER_HMAC_CONTENT_LENGTH = 'Content-Length';
export const HEADER_HMAC_OVERRIDE = 'X-Hmac-Override';
export const HEADER_HMAC_SIGNATURE = 'X-Hmac-Signature';
export const HEADER_HMAC_TIMESTAMP = 'X-Hmac-Timestamp';
/** Response Headers */
export const HEADER_ACCESS_CONTROL_ALLOW_ORIGIN = 'Access-Control-Allow-Origin';
export const HEADER_ACCESS_CONTROL_ALLOW_CREDENTIALS = 'Access-Control-Allow-Credentials';
export const HEADER_REQUEST_META = 'X-Request-Meta';
export const HEADER_REQUEST_ID = 'X-Request-Id';
export const HEADER_RESPONSE_TYPE = 'X-Response-Type';
export const HEADER_POWERED_BY = 'X-Powered-By';
export const HEADER_RETRY_AFTER = 'Retry-After';
export const HEADER_SERVICE_VERSION = 'X-Service-Version';
/** Content Types */
export const CONTENT_TYPE_JSON = 'application/json';
export const CONTENT_TYPE_TEXT = 'text/plain';

/** Default timeout for HTTP requests in milliseconds (15 seconds). */
export const HTTP_REQUEST_TIMEOUT_MS = 15000;

export enum HttpMethod {
  DELETE = 'DELETE',
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT'
}

export interface ApiConfig {
  [service: string]: {
    baseUrl: string;
    hasHmac?: boolean; //  Default is false. If true, HTTP client will need to fetch HMAC token + timestamp during load. Set to true if service has HMAC authentication (via hasHmacAuth or forceHmac in manifest).
  };
}

/** TODO - This (probably) should not be "any". */
export interface IHttpClient {
  delete<T = any>(service: string, authMethod: string, path: string, options?: IHttpClientOptions): Promise<T>;
  get<T = any>(service: string, authMethod: string, path: string, options?: IHttpClientOptions): Promise<T>; // POST is allowed to have "void" payload (hence payload is optional).
  patch<T = any>(
    service: string,
    authMethod: string,
    path: string,
    payload: any,
    options?: IHttpClientOptions
  ): Promise<T>;
  post<T = any>(
    service: string,
    authMethod: string,
    path: string,
    payload?: any,
    options?: IHttpClientOptions
  ): Promise<T>;
  put<T = any>(
    service: string,
    authMethod: string,
    path: string,
    payload: any,
    options?: IHttpClientOptions
  ): Promise<T>;
  setAuthData(
    authData: {
      accessToken?: string;
      cognitoUsername: string;
      email: string;
      emailVerified: boolean;
      exp: number;
      expiresIn?: number;
      iat: number;
      idToken: string;
      refreshToken: string;
      sub: string;
      tokenType: string;
    } | null
  ): void;
  terminate(): void;
}

export interface IHttpClientOptions {
  encrypt?: HttpRequestEncryption;
  headers?: Record<string, string>;
  params?: QueryParamsType; // In milliseconds. Default is 15000 (15 seconds).
  retry?: HttpRetryOptions;
  signal?: AbortSignal;
  timeout?: number;
}

/** HTTP status codes that should trigger a retry (transient server/gateway failures). */
export const RETRYABLE_HTTP_STATUS_CODES = [408, 429, 500, 502, 503, 504] as const;

export interface HttpRetryOptions {
  /** Base delay in milliseconds for exponential backoff. Default: 1000 */
  baseDelayMs?: number;
  /** Maximum delay in milliseconds between retries. Default: 10000 */
  maxDelayMs?: number;
  /** Maximum number of retry attempts. Default: 3 */
  maxRetries?: number;
  /** HTTP status codes that should trigger a retry. Default: [408, 429, 500, 502, 503, 504] */
  retryableStatusCodes?: number[];
}

/** Options interface for SDK methods to configure request behavior. */
export interface ISdkOptions extends Omit<IHttpClientOptions, 'headers' | 'params'> {}

export interface HttpRequestEncryption {
  /** If true, request payloads will be encrypted before sending and decrypted on the server. */
  request?: boolean;
  /** If true, response payloads will be encrypted on the server and decrypted after receiving. */
  response?: boolean;
}

/** TODO - Does this belong here? */
export interface DomainFragments {
  domain: string;
  fqdn: string;
  path: string;
  pathFragments: string[];
  subdomain: string;
}

/** Response Types. */
export enum ResponseType {
  ACCEPTED = 'ACCEPTED', // 202
  ACCEPTED_WITH_DATA = 'ACCEPTED_WITH_DATA', // 202
  BAD_GATEWAY = 'BAD_GATEWAY', // 502
  BAD_REQUEST = 'BAD_REQUEST', // 400
  BAD_REQUEST_WITH_DATA = 'BAD_REQUEST_WITH_DATA', // 400
  CONFLICT = 'CONFLICT', // 409
  CONFLICT_WITH_DATA = 'CONFLICT_WITH_DATA', // 409
  CREATED = 'CREATED', // 201
  CREATED_WITH_DATA = 'CREATED_WITH_DATA', // 201
  FORBIDDEN = 'FORBIDDEN', // 403
  FORBIDDEN_WITH_DATA = 'FORBIDDEN_WITH_DATA', // 403
  FORM_ERROR = 'FORM_ERROR', // 422
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT', // 504
  NO_CONTENT = 'NO_CONTENT', // 204
  NOT_FOUND = 'NOT_FOUND', // 404
  PAGINATED_CURSOR = 'PAGINATED_CURSOR', // 200
  PAGINATED_OFFSET = 'PAGINATED_OFFSET', // 200 (blocked pending RDS support — ResponseBuilder.paginatedOffset throws; BF-356)
  SERVER_ERROR = 'SERVER_ERROR', // 500
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE', // 503
  SUCCESS = 'SUCCESS', // 200
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS', // 429
  TOO_MANY_REQUESTS_WITH_DATA = 'TOO_MANY_REQUESTS_WITH_DATA', // 429
  UNAUTHORIZED = 'UNAUTHORIZED', // 401
  UNAUTHORIZED_WITH_DATA = 'UNAUTHORIZED_WITH_DATA' // 401
}

export enum ServiceRequestTypesEnum {
  Request = 'Request'
}

/** If you update this, make sure to update the type: ServiceReturnTypes */
export enum ServiceReturnTypesEnum {
  AcceptedResponse = 'AcceptedResponse',
  AcceptedResponseWithData = 'AcceptedResponseWithData',
  BadGatewayResponse = 'BadGatewayResponse',
  BadRequestResponse = 'BadRequestResponse',
  BadRequestResponseWithData = 'BadRequestResponseWithData',
  ConflictResponse = 'ConflictResponse',
  ConflictResponseWithData = 'ConflictResponseWithData',
  CreatedResponse = 'CreatedResponse',
  CreatedResponseWithData = 'CreatedResponseWithData',
  ForbiddenResponse = 'ForbiddenResponse',
  ForbiddenResponseWithData = 'ForbiddenResponseWithData',
  FormErrorResponse = 'FormErrorResponse',
  GatewayTimeoutResponse = 'GatewayTimeoutResponse',
  NotFoundResponse = 'NotFoundResponse',
  PaginatedCursorResponse = 'PaginatedCursorResponse',
  PaginatedOffsetResponse = 'PaginatedOffsetResponse',
  ServerErrorResponse = 'ServerErrorResponse',
  ServiceUnavailableResponse = 'ServiceUnavailableResponse',
  SuccessResponse = 'SuccessResponse',
  SuccessWithNoContentResponse = 'SuccessWithNoContentResponse',
  TooManyRequestsResponse = 'TooManyRequestsResponse',
  TooManyRequestsResponseWithData = 'TooManyRequestsResponseWithData',
  UnauthorizedResponse = 'UnauthorizedResponse',
  UnauthorizedResponseWithData = 'UnauthorizedResponseWithData'
}

/** If you update this, make sure to update const: ServiceReturnTypesEnum */
export type ServiceReturnTypes =
  | SuccessResponse<any>
  | SuccessWithNoContentResponse
  | PaginatedOffsetResponse<any>
  | PaginatedCursorCallbackResponse<any>
  | PaginatedCursorResponse<any>
  | CreatedResponse
  | CreatedResponseWithData<any>
  | AcceptedResponse
  | AcceptedResponseWithData<any>
  | BadRequestResponse
  | BadRequestResponseWithData<any>
  | UnauthorizedResponse
  | UnauthorizedResponseWithData<any>
  | ForbiddenResponse
  | ForbiddenResponseWithData<any>
  | NotFoundResponse
  | ConflictResponse
  | ConflictResponseWithData<any>
  | TooManyRequestsResponse
  | TooManyRequestsResponseWithData<any>
  | FormErrorResponse<any>
  | ServerErrorResponse
  | BadGatewayResponse
  | ServiceUnavailableResponse
  | GatewayTimeoutResponse;

export type ServiceFailureReturnTypes =
  | BadRequestResponse
  | BadRequestResponseWithData<any>
  | UnauthorizedResponse
  | UnauthorizedResponseWithData<any>
  | ForbiddenResponse
  | ForbiddenResponseWithData<any>
  | NotFoundResponse
  | ConflictResponse
  | ConflictResponseWithData<any>
  | TooManyRequestsResponse
  | TooManyRequestsResponseWithData<any>
  | FormErrorResponse<any>
  | ServerErrorResponse
  | BadGatewayResponse
  | ServiceUnavailableResponse
  | GatewayTimeoutResponse;

/** These are the only return types that can have data masked. */
export type MaskableServiceReturnTypes =
  | SuccessResponse<any>
  | PaginatedOffsetResponse<any>
  | PaginatedCursorCallbackResponse<any>
  | PaginatedCursorResponse<any>
  | CreatedResponseWithData<any>
  | AcceptedResponseWithData<any>;

/** Status codes for ServiceFailureReturnTypes (4xx and 5xx errors) */
export const SERVICE_FAILURE_STATUS_CODES: number[] = [400, 401, 403, 404, 409, 422, 429, 500, 502, 503, 504];

export interface ResponseBase<S extends number = never, T extends ResponseType = never> {
  responseType: T;
  statusCode: S;
}

/** Used for GET requests that return a resource/response. If you want to return a 200 but with no data, use 204. */
export interface SuccessResponse<T = never> extends ResponseBase<200, ResponseType.SUCCESS> {
  data: T;
}

/** Offset-based pagination response. */
export interface PaginatedOffsetResponse<T = never> extends ResponseBase<200, ResponseType.PAGINATED_OFFSET> {
  data: T[];
  pagination: PaginatedOffsetResponsePagination;
}

export interface PaginatedOffsetResponsePagination {
  hasNextPage: boolean; // True if there are more pages.
  hasPreviousPage: boolean; // True if there are previous pages.
  limit: number; // Number of items per page.
  page: number; // Current page number.
  totalItems: number; // Total number of items.
  totalPages: number; // Total number of pages.
}

/** Cursor-based pagination response (public SDK type with encoded cursor). */
export interface PaginatedCursorResponse<T = never> extends ResponseBase<200, ResponseType.PAGINATED_CURSOR> {
  data: T[];
  pagination: PaginatedCursorResponsePagination;
}

export interface PaginatedCursorResponsePagination {
  cursor: string | null; // Opaque cursor for fetching next page. Null if no more pages.
  hasMore: boolean; // True if there are more pages.
  limit: number; // Number of items per page.
}

/** Cursor-based pagination response (callback return type with raw lastEvaluatedKey). */
export interface PaginatedCursorCallbackResponse<T = never> extends ResponseBase<200, ResponseType.PAGINATED_CURSOR> {
  data: T[];
  pagination: PaginatedCursorCallbackResponsePagination;
}

export interface PaginatedCursorCallbackResponsePagination {
  cursor: Record<string, unknown> | null; // Raw DynamoDB lastEvaluatedKey. Null if no more pages.
  hasMore: boolean; // True if there are more pages.
  limit: number; // Number of items per page.
}

/** Query-param name carrying the opaque cursor for cursor-based pagination. */
export const PAGINATION_PARAM_CURSOR = 'cursor';
/** Query-param name carrying the page-size limit. */
export const PAGINATION_PARAM_LIMIT = 'limit';
/** Query-param name carrying the 1-based page number for offset-based pagination. */
export const PAGINATION_PARAM_PAGE = 'page';

/**
 * Query-param names the framework injects and strips before schema validation on a **paginated**
 * endpoint. Such an endpoint must NOT declare these in its `QueryParams` schema — the validator rejects
 * them. A non-paginated endpoint may declare them freely; they are ordinary params there.
 */
export const RESERVED_PAGINATION_PARAMS: readonly string[] = [
  PAGINATION_PARAM_CURSOR,
  PAGINATION_PARAM_LIMIT,
  PAGINATION_PARAM_PAGE
];

/**
 * Fallback pagination limits the Lambda runtime applies when an endpoint's `pagination` config omits
 * explicit values. The SDK generator reads the same constants so `endpointMeta.pagination` always
 * advertises exactly what `@blufin/api`'s `PaginationHandler` will enforce.
 */
export const PAGINATION_DEFAULTS = { defaultLimit: 10, maxLimit: 500 } as const;

/** Pagination query params for offset-based pagination. */
export interface OffsetPaginationParams {
  limit?: number;
  page?: number;
}

/** Pagination query params for cursor-based pagination. */
export interface CursorPaginationParams {
  cursor?: string;
  limit?: number;
}

/** Compile-time guard helper — `Assert<false>` is a type error. */
type Assert<T extends true> = T;

/** Fails to typecheck if `CursorPaginationParams` gains a key that is not a reserved pagination param. */
export type CursorPaginationParamsAreReserved = Assert<
  [Exclude<keyof CursorPaginationParams, typeof PAGINATION_PARAM_CURSOR | typeof PAGINATION_PARAM_LIMIT>] extends [
    never
  ]
    ? true
    : false
>;

/** Fails to typecheck if `OffsetPaginationParams` gains a key that is not a reserved pagination param. */
export type OffsetPaginationParamsAreReserved = Assert<
  [Exclude<keyof OffsetPaginationParams, typeof PAGINATION_PARAM_LIMIT | typeof PAGINATION_PARAM_PAGE>] extends [never]
    ? true
    : false
>;

/** Summary of pagination configuration for documentation/UI purposes. */
export interface ServiceEndpointPaginationSummary {
  defaultLimit?: number;
  maxLimit?: number;
  type: 'cursor' | 'offset';
}

/** Used for POST requests that create a new resource, where the ID of the resource is immediately available. */
export interface CreatedResponse extends ResponseBase<201, ResponseType.CREATED> {
  resourceId: number; // ID of the created resource. Should be able to find the resource again using this ID.
}

/** Used for POST requests that create a new resource, where the ID of the resource is immediately available, but also returns data to the client. */
export interface CreatedResponseWithData<T = never> extends ResponseBase<201, ResponseType.CREATED_WITH_DATA> {
  data: T; // Data to return to the client.
  resourceId: number;
}

/** Used for POST requests that send a payload, where the payload is accepted but the resource/result is not immediately available. */
export interface AcceptedResponse extends ResponseBase<202, ResponseType.ACCEPTED> {}

/** Used for POST requests that send a payload, where the payload is accepted but the resource/result is not immediately available, but also returns data to the client. */
export interface AcceptedResponseWithData<T = never> extends ResponseBase<202, ResponseType.ACCEPTED_WITH_DATA> {
  data: T;
}

/**
 * Used for DELETE requests that were successfully processed, but there’s no content to return to the client.
 * Can also use for any 200 requests that don't return data (eg: successful PUT requests).
 */
export interface SuccessWithNoContentResponse extends ResponseBase<204, ResponseType.NO_CONTENT> {}

/** Used for any requests that can't be processed due to bad data from the client. */
export interface BadRequestResponse extends ResponseBase<400, ResponseType.BAD_REQUEST> {
  reason: string; // Explanation of the bad request.
}

/** Used for any requests that can't be processed due to bad data from the client, but also returns data to the client. */
export interface BadRequestResponseWithData<T = never> extends ResponseBase<400, ResponseType.BAD_REQUEST_WITH_DATA> {
  data: T; // Data to return to the client.
  reason: string;
}

/** The client has not provided valid authentication credentials. */
export interface UnauthorizedResponse extends ResponseBase<401, ResponseType.UNAUTHORIZED> {}

/** The client has not provided valid authentication credentials, but also returns data to the client. */
export interface UnauthorizedResponseWithData<T = never>
  extends ResponseBase<401, ResponseType.UNAUTHORIZED_WITH_DATA> {
  data: T;
}

/** The client is authenticated but does not have permission to access the requested resource. */
export interface ForbiddenResponse extends ResponseBase<403, ResponseType.FORBIDDEN> {
  reason: string; // Reason why the client is forbidden from accessing the resource.
}

/**
 * 403 Forbidden response that carries structured data alongside a reason. Useful when callers need
 * a machine-readable discriminator (eg: an error code enum) to branch on the denial cause.
 */
export interface ForbiddenResponseWithData<T = never> extends ResponseBase<403, ResponseType.FORBIDDEN_WITH_DATA> {
  data: T;
  reason: string;
}

/** The requested resource could not be found. */
export interface NotFoundResponse extends ResponseBase<404, ResponseType.NOT_FOUND> {}

/** Use when request could not be completed due to a conflict with the current state of the resource, eg: duplicate entry. */
export interface ConflictResponse extends ResponseBase<409, ResponseType.CONFLICT> {
  reason: string; // Explanation of the conflict, eg: "Username already exists."
}

/**
 * 409 Conflict response that carries structured data alongside a reason. Useful for optimistic-locking
 * conflicts where the client needs the current server-side version/state to recover.
 */
export interface ConflictResponseWithData<T = never> extends ResponseBase<409, ResponseType.CONFLICT_WITH_DATA> {
  data: T;
  reason: string;
}

/** Use when the request was rejected because the client exceeded a rate/quota limit (eg: per-owner asset quota). */
export interface TooManyRequestsResponse extends ResponseBase<429, ResponseType.TOO_MANY_REQUESTS> {
  reason: string; // Explanation of the limit that was hit, eg: "Asset quota exceeded."
}

/**
 * 429 Too Many Requests response that carries structured data alongside a reason. Useful when the
 * client needs a machine-readable discriminator (eg: which quota was exceeded, or a retry hint).
 */
export interface TooManyRequestsResponseWithData<T = never>
  extends ResponseBase<429, ResponseType.TOO_MANY_REQUESTS_WITH_DATA> {
  data: T;
  reason: string;
}

/** TODO: Placeholder - Need to figure this out. (Maybe rename to UnprocessableEntityResponse?) */
/** The server understands the request's content type and syntax, but the content itself has semantic issues (eg: form validation did not pass). */
export interface FormErrorResponse<T = never> extends ResponseBase<422, ResponseType.FORM_ERROR> {
  obj: T; // Not sure yet what this should be... ?
}

/** The server encountered an unexpected condition that prevented it from fulfilling the request. */
export interface ServerErrorResponse extends ResponseBase<500, ResponseType.SERVER_ERROR> {
  error: string;
}

/** The server, acting as a gateway, got an invalid or unauthorized response from an upstream server (eg: the Grafana instance). */
export interface BadGatewayResponse extends ResponseBase<502, ResponseType.BAD_GATEWAY> {
  reason: string;
}

/** The server is currently unable to handle the request due to a temporary overloading or maintenance. */
export interface ServiceUnavailableResponse extends ResponseBase<503, ResponseType.SERVICE_UNAVAILABLE> {
  reason: string;
}

/** The server did not receive a timely response from an upstream server (Lambda timeout). */
export interface GatewayTimeoutResponse extends ResponseBase<504, ResponseType.GATEWAY_TIMEOUT> {
  error: string;
}

/** Timeout error thrown when an HTTP request exceeds the configured timeout duration. */
export interface TimeoutError {
  message: string;
  name: 'TimeoutError';
  timeoutMs: number;
}
